/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* The modal card carries onClick={e => e.stopPropagation()} so a click inside it
   doesn't reach the overlay's close handler. That's event containment, not an
   interactive control, so there is no meaningful keyboard equivalent to add. */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
// eslint-disable-line jsx-a11y/no-static-element-interactions
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import moment from 'moment';
import { CircularProgress as MuiCircularProgress } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { dataFetcher, endpoints } from 'Api';
import { VideoPreview } from 'Components';
import PermissionDeniedModal from 'Components/PermissionDeniedModal';
import { Pathname } from 'Routes';
import cn from 'classnames';
import { AppContext } from 'Context';
import ImageGroup from 'Components/ImageGroup/ImageGroup';
import LockIcon from '@mui/icons-material/Lock';
import { ReactComponent as BackIcon } from 'Assets/chevron-left.svg';
import { ReactComponent as PlayIcon } from 'Assets/play.svg';
import WatchlistIcon from 'Assets/watchlistIcon.svg';
import backArrowIcon from 'Assets/backArrowIcon.svg';
import AlterImage from 'Assets/alter-image.png';
import Aurora from 'Components/reactbits/Aurora';
import RowCarousel from 'Components/reactbits/RowCarousel';
import { AnimatedPlay, AnimatedBookmark, AnimatedChevronLeft } from 'Components/reactbits/AnimatedIcons';
import { useViewerMode } from 'hooks/useViewerMode';
import styles from './index.module.css';

const CircularProgress = withStyles({
  circle: {
    color: '#fff !important',
  },
})(MuiCircularProgress);

export const VideoModal = ({
  videoID: videoIDProp, isFromGift: isFromGiftProp, onClose, onOpenVideo, navContext,
} = {}) => {
  const { setAppSnackbar, setContextLoading, setVisible, userDetails, subscriptionDetails } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const videoID = videoIDProp || params.videoID;
  const [searchParams] = useSearchParams();
  const isFromGift = isFromGiftProp !== undefined
    ? isFromGiftProp
    : searchParams.get('fromGift') === 'true';
  // In modal mode (opened in-place from /profile) the context arrives via the
  // navContext prop; in full-page mode it comes from router location.state.
  const {
    from, playlistName, playlist_id, category_id, videoSortBy, videoSortOrder, vpm,
  } = navContext || location.state || {};
  // Carry the playlist/category + sort context across related/next/player
  // navigation so the v2 endpoint can keep returning the correct ordered list.
  const relatedNavState = from
    ? {
      state: {
        from, playlist_id, category_id, playlistName, videoSortBy, videoSortOrder, vpm,
      },
    }
    : undefined;
  const [videoDetails, setVideoDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const { isFullscreen, toggle: toggleViewerMode } = useViewerMode();
  const [isVideoInWatchlist, setVideoInWatchlist] = useState(false);
  const [updatingInWatchlist, setWatchlistUpdationStatus] = useState(false);
  const [previewType, setPreviewType] = useState(1);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  // The scrolling element of the modal (both the skeleton and the loaded card
  // render into it), so a video switch can reset the scroll position.
  const overlayRef = useRef(null);

  // Hide the Zendesk chat bubble. This used to sit in the render body, so every
  // single render performed a `javascript:` URL navigation — including the one
  // that lands when the detail fetch resolves, which is what made the screen
  // flash a beat after switching videos. Once on mount is all it ever needed.
  useEffect(() => {
    try {
      // eslint-disable-next-line no-script-url
      window.location.href = 'javascript: $zopim.livechat.button.hide()';
    } catch (e) { /* chat widget not loaded */ }
  }, []);
  const { mutate: updateInWatchlist } = useSWR(
    [endpoints.updateWatchList, videoID, isVideoInWatchlist],
    {
      fetcher: (url, id, shouldDelete) => dataFetcher(url, {
          video_id: id,
          action: shouldDelete ? 'delete' : 'add',
        }),
      onSuccess: ({ success }) => {
        if (success) {
          setVideoInWatchlist(!isVideoInWatchlist);
        } else {
          setAppSnackbar({
            isVisible: true,
            type: 'error',
            message: 'Oops! Something went wrong',
          });
        }

        setWatchlistUpdationStatus(false);
      },
      onError: () => {
        setWatchlistUpdationStatus(false);
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong',
        });
      },
    }
  );

  const { isValidating: gettingVideos, mutate: getVideoDetails } = useSWR(
    [endpoints.getVideosDetailV2, videoID, from, playlist_id, category_id],
    {
      fetcher: (url, id, fromParam) => dataFetcher(url, {
        id,
        limit: 10,
        ...(fromParam && { from: fromParam }),
        ...(playlist_id && { playlist_id }),
        ...(category_id && { category_id }),
        ...(videoSortBy && { videoSortBy }),
        ...(videoSortOrder && { videoSortOrder }),
        ...(typeof vpm === 'boolean' && { vpm }),
      }),
      onSuccess: ({ success, data, banner_preview_style_id }) => {
        if (success) {
          setVideoDetails((prev) => {
            const prevList = prev?.videos;
            const nextList = data?.videos;
            // Merge rather than replace: keep the objects (and therefore the
            // exact thumbnail URLs) the row is already painting, append only
            // ids we haven't seen, and never drop one. A response that adds
            // nothing hands back the SAME array reference, so the carousel
            // isn't touched at all — no re-decoded thumbnails, no reflow.
            // (The current video is hidden by relatedVideos' filter, not by
            // removing it here, so switching back and forth stays stable.)
            const mergedList = (() => {
              if (!Array.isArray(nextList)) return prevList;
              if (!Array.isArray(prevList) || !prevList.length) return nextList;
              const seen = new Set(prevList.map((o) => String(o.id)));
              const added = nextList.filter((n) => !seen.has(String(n.id)));
              return added.length ? [...prevList, ...added] : prevList;
            })();
            // Keep the thumbnail the hero is already painting for THIS video.
            // The detail response can hand back a different URL for the same
            // image (different size, or a freshly signed S3 link), and swapping
            // a background-image makes the browser re-fetch and re-decode it —
            // a visible pop a beat after the switch.
            const keepThumb = prev?.id !== undefined
              && String(prev.id) === String(data?.id)
              && prev?.thumbnails?.[0];
            const next = {
              ...data,
              ...(keepThumb ? { thumbnails: prev.thumbnails } : {}),
              videos: mergedList,
            };
            // If the response says exactly what the hero is already showing —
            // the normal case after openRelated seeded it from the list item —
            // hand back the SAME object so React bails out of the update
            // entirely. Otherwise every switch paints a second, identical frame
            // a beat later, and re-rasterising the card's backdrop-filter and
            // the full-bleed preview reads as a blink.
            try {
              if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
            } catch (e) { /* fall through to the update */ }
            return next;
          });
          // Normalised: an undefined field would flip this state from `false`
          // to `undefined` — a state change as far as React is concerned, so it
          // forced a re-render (and changed the watchlist SWR key) for nothing.
          setVideoInWatchlist(!!data?.isInWatchlist);
          // setPreviewType(parseInt(banner_preview_style_id, 10));
        } else {
          setAppSnackbar({
            isVisible: true,
            type: 'error',
            message: 'Oops! Something went wrong',
          });
        }

        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong',
        });
        // Opened in place (from the profile): don't yank the user to /access on
        // a failed detail fetch — just close the modal. Only the standalone
        // /video route falls back to navigating.
        if (onClose) onClose();
        else navigate(Pathname.access);
      },
    }
  );

  // Re-fetch whenever the id changes, not just on mount. Clicking a tile in the
  // related row navigates to /video/<other id>, which re-renders THIS component
  // with a new param rather than remounting it — with `[]` deps the hero kept
  // showing the previous video's thumbnail/title/description while Play (which
  // reads the live `videoID`) already pointed at the newly clicked video.
  // The SWR hook can't cover this either: revalidateOnMount is globally false.
  useEffect(() => {
    // Only fall back to the skeleton when we genuinely have nothing to show for
    // this id. A click in the related row seeds the hero synchronously (see
    // openRelated below), so the swap is instant and the fetch just fills in
    // the fields the list item doesn't carry.
    if (String(videoDetails?.id ?? '') !== String(videoID)) setLoading(true);
    getVideoDetails();
    // Land on the hero — the click that got here happened at the bottom of the
    // scrolled overlay.
    overlayRef.current?.scrollTo?.({ top: 0 });
  }, [videoID]);

  const isVideoLockedForUser = (lockedFor) => {
    try {
      if (!lockedFor) return false;
      const currentPlanId = subscriptionDetails?.subscription?.plan_id
        || subscriptionDetails?.plans?.[subscriptionDetails?.plans?.length - 1]?.plan_id;
      if (!currentPlanId) return false;
      const lockedPlanIds = String(lockedFor).split(',').map((id) => id.trim());
      return lockedPlanIds.includes(String(currentPlanId));
    } catch (error) {
      return false;
    }
  };

  const isMainLocked = isVideoLockedForUser(videoDetails?.locked_for);
  const hasVideoPermission = () => !isMainLocked;

  const handlePlayClick = (e) => {
    e.preventDefault();
    if (!isMainLocked) {
      navigate(
        `${Pathname.getPlayerPath(videoID, 'play')}${isFromGift ? '?fromGift=true' : ''}`,
        relatedNavState
      );
    } else {
      setShowPermissionModal(true);
    }
  };

  const handleResumeClick = (e) => {
    e.preventDefault();
    if (!isMainLocked) {
      navigate(
        `${Pathname.getPlayerPath(videoID, 'resumeWatching')}${isFromGift ? '?fromGift=true' : ''}`,
        relatedNavState
      );
    } else {
      setShowPermissionModal(true);
    }
  };

  // Dismiss the modal → back to where we came from (same logic as the old back button).
  const handleClose = onClose || (() => {
    setVisible(true);
    const prevLocation = localStorage.getItem('prevlocation');
    if (prevLocation === 'innerpage') {
      navigate(`/innerpage${isFromGift ? '?fromGift=true' : ''}`, {
        state: {
          title: videoDetails?.Category,
          id: videoDetails?.category_id,
          profile_id: videoDetails?.user_id,
        },
      });
    } else {
      const profilePath = sessionStorage.getItem('navigateProfile');
      if (isFromGift && profilePath && !profilePath.includes('fromGift')) {
        navigate(`${profilePath}${profilePath.includes('?') ? '&' : '?'}fromGift=true`);
      } else {
        navigate(profilePath || Pathname.access);
      }
    }
    setContextLoading(true);
  });

  const relatedVideos = (videoDetails?.videos || []).filter((v) => String(v.id) !== String(videoID));
  const relThumb = (v) => {
    try {
      return (Array.isArray(v?.thumbnails) ? v.thumbnails[0] : JSON.parse(v?.thumbnails)?.[0]) || AlterImage;
    } catch (e) {
      return AlterImage;
    }
  };
  // Switch to a related video. The list item already carries everything the
  // hero shows first (title, thumbnail, and usually description / paused_at /
  // locked_for), so paint it immediately and let the detail fetch reconcile the
  // rest — no skeleton flash between two videos of the same playlist.
  const openRelated = (v) => {
    setVideoDetails((prev) => ({
      ...v,
      // Normalised to the array shape the hero reads; list items sometimes
      // carry `thumbnails` as a JSON string.
      thumbnails: [relThumb(v)],
      // The carousel is the playlist we're already inside — keep it mounted
      // instead of letting it blink out until the fetch lands.
      videos: prev?.videos,
      // Row heading context. Only these two fall back to the previous video —
      // within one playlist they're the same, and blanking them would collapse
      // the subtitle line for a frame. Every other field comes from the clicked
      // item alone, so the hero never shows the previous video's text and then
      // silently corrects itself.
      Category: v?.Category ?? prev?.Category,
      SubCategory: v?.SubCategory ?? prev?.SubCategory,
    }));
    // Carry the playlist/category context over. Without it `from` goes undefined
    // on the next render, the fetcher stops sending it, and the server answers
    // with a category-based list instead of this playlist — so the row genuinely
    // re-populated (and every tile re-fetched its thumbnail) a beat after the
    // switch. It also kept the "More from <playlist>" heading.
    if (onOpenVideo) {
      onOpenVideo(v.id, {
        from, playlist_id, category_id, playlistName, videoSortBy, videoSortOrder, vpm,
      });
    } else {
      navigate(
        `${Pathname.getVideoPath(v.id)}${isFromGift ? '?fromGift=true' : ''}`,
        relatedNavState
      );
    }
  };

  // Magic Bento glow — track the cursor within a tile.
  const handleCardGlow = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--gx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--gy', `${e.clientY - r.top}px`);
  };

  return (
    <>
      {loading ? (
        <div ref={overlayRef} className={cn(styles.vmOverlay, { [styles.vmOverlayFull]: isFullscreen })} onClick={handleClose}>
          <div className={cn(styles.vmCard, { [styles.vmCardFull]: isFullscreen })} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.vmClose}
              onClick={handleClose}
              aria-label="Close"
            >
              <AnimatedChevronLeft size={22} />
            </button>
            <button
              type="button"
              className={styles.vmExpand}
              onClick={(e) => { e.stopPropagation(); toggleViewerMode(); }}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                  <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                  <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                  <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                  <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                  <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                  <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
              )}
            </button>
            <div className={styles.vmCardLoading}>
              <div className={styles.vmShimmer} />
            </div>
          </div>
        </div>
      ) : (
        <div ref={overlayRef} className={cn(styles.vmOverlay, { [styles.vmOverlayFull]: isFullscreen })} onClick={handleClose}>
          <div
            className={cn(styles.vmCard, { [styles.vmCardFull]: isFullscreen })}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className={styles.vmClose}
              onClick={handleClose}
              aria-label="Close"
            >
              <AnimatedChevronLeft size={22} />
            </button>
            <button
              type="button"
              className={styles.vmExpand}
              onClick={(e) => { e.stopPropagation(); toggleViewerMode(); }}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                  <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                  <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                  <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                  <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                  <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                  <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
              )}
            </button>
            <div className={styles.vmTop}>
              <div
                className={cn(styles.vmPreview, { [styles.vmPreviewBlurred]: isMainLocked })}
                style={{ backgroundImage: `url(${videoDetails?.thumbnails?.[0] || AlterImage})` }}
              >
                {isMainLocked && (
                  <div className={styles.vmLock}><LockIcon /></div>
                )}
              </div>
              <div className={styles.vmInfo}>
                <h1 className={styles.vmTitle}>{videoDetails?.title}</h1>
                <div className={styles.vmMeta}>
                  {videoDetails?.Category}
                  {videoDetails?.Category && videoDetails?.SubCategory ? ' · ' : ''}
                  {videoDetails?.SubCategory}
                </div>
                {videoDetails?.description ? (
                  <p className={styles.vmDesc}>{videoDetails.description}</p>
                ) : null}
                <div className={styles.vmActions}>
                  {hasVideoPermission(videoID) ? (
                    <>
                      {/* The label and the action have to agree: a button that
                          reads "Resume" must open the player in resumeWatching
                          mode, or it restarts the video from 0. */}
                      <button
                        type="button"
                        className={styles.vmPlay}
                        onClick={videoDetails?.paused_at > 0 ? handleResumeClick : handlePlayClick}
                      >
                        <AnimatedPlay size={18} />
                        {videoDetails?.paused_at > 0 ? 'Resume' : 'Play'}
                      </button>
                      <button
                        type="button"
                        className={cn(styles.vmWatch, { [styles.vmWatchOn]: isVideoInWatchlist })}
                        disabled={updatingInWatchlist}
                        onClick={updateInWatchlist}
                      >
                        <AnimatedBookmark size={18} checked={isVideoInWatchlist} />
                        {isVideoInWatchlist ? 'In Watchlist' : 'Watchlist'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={styles.vmUpgrade}
                      onClick={() => setShowPermissionModal(true)}
                    >
                      Upgrade Plan
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.vmDivider} />
            <div className={styles.vmRelated}>
              <div className={styles.vmRelatedTitle}>
                {from === 'playlist' && playlistName ? `More from ${playlistName}` : videoDetails?.Category}
              </div>
              {/* Keep the row on screen while a switch revalidates — we carry the
                  playlist over from the previous video, so "Loading…" is only
                  for a cold load with nothing to show yet. */}
              {!relatedVideos.length ? (
                <div className={styles.vmLoading}>
                  {gettingVideos ? 'Loading…' : 'No related videos'}
                </div>
              ) : (
                <RowCarousel gap={14}>
                  {relatedVideos.map((v) => (
                    <div
                      key={v.id}
                      className={styles.vmRelTile}
                      role="button"
                      tabIndex={0}
                      onMouseMove={handleCardGlow}
                      style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.55)), url(${relThumb(v)})` }}
                      onClick={() => openRelated(v)}
                    >
                      <span className={styles.vmRelName}>{v.title}</span>
                    </div>
                  ))}
                </RowCarousel>
              )}
            </div>
          </div>
        </div>
      )}
      <PermissionDeniedModal
        open={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
      />
    </>
  );
};

// Route entry (/video/:id) — renders the modal full-screen with its own
// back-navigation. In-app, the profile renders <VideoModal> in place instead.
export const VideoDetails = () => <VideoModal />;
